import { createClient } from '@supabase/supabase-js'
import { LANDING_HOST, isMeetepioHost, authRedirectBase } from './hosts'

const url = process.env.REACT_APP_SUPABASE_URL || ''
const key = process.env.REACT_APP_SUPABASE_ANON_KEY || ''
export const supabaseConfigured = !!(url && key)

// ── Session storage ───────────────────────────────────────────────────────
// On meetepio.com hosts we store sessions in a cookie scoped to
// `.meetepio.com` so a single sign-in works across both apex
// (meetepio.com) and the app subdomain (app.meetepio.com). Default
// localStorage is per-origin, which would log users out the moment the
// apex → subdomain redirect fires.
//
// Localhost, Vercel preview URLs, and the legacy getepisode.ai domain
// all keep using localStorage (the Supabase default) — no cookie
// domain is shared and dev doesn't need cross-origin auth.

const COOKIE_DOMAIN = `.${LANDING_HOST}`;

// Per-cookie value cap. Browsers reject values above ~4096 bytes; we
// leave headroom for the rest of the Set-Cookie header (Path, Domain,
// Expires, SameSite, Secure). Sessions that overflow get chunked across
// `${name}.0`, `${name}.1`, … and reassembled on read. Without this,
// large Supabase sessions (JWT + refresh token + user metadata) would
// silently fail to write, leaving the user logged-out after the apex →
// app.meetepio.com redirect.
const CHUNK_SIZE = 3200;
const MAX_CHUNKS = 20;

function readRawCookie(name) {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    return part.slice(eq + 1);
  }
  return null;
}

function writeRawCookie(name, encodedValue) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + 365 * 24 * 3600 * 1000).toUTCString();
  document.cookie =
    `${name}=${encodedValue}; Path=/; Domain=${COOKIE_DOMAIN}; ` +
    `Expires=${expires}; SameSite=Lax; Secure`;
}

function deleteRawCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${name}=; Path=/; Domain=${COOKIE_DOMAIN}; ` +
    `Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

// Clear any chunked variants of `name`. Bounded loop so a flapping
// cookie can't run forever.
function deleteChunks(name) {
  for (let i = 0; i < MAX_CHUNKS; i++) {
    const k = `${name}.${i}`;
    if (readRawCookie(k) == null) break;
    deleteRawCookie(k);
  }
}

const cookieStorage = {
  getItem(name) {
    // Single-cookie fast path. If the value fits in one cookie, this
    // is what Supabase wrote.
    const single = readRawCookie(name);
    if (single != null) {
      try { return decodeURIComponent(single); }
      catch { return null; }
    }
    // Otherwise reassemble from chunks.
    let assembled = '';
    for (let i = 0; i < MAX_CHUNKS; i++) {
      const part = readRawCookie(`${name}.${i}`);
      if (part == null) break;
      assembled += part;
    }
    if (!assembled) return null;
    try { return decodeURIComponent(assembled); }
    catch { return null; }
  },
  setItem(name, value) {
    if (typeof document === 'undefined') return;
    const encoded = encodeURIComponent(value);
    if (encoded.length <= CHUNK_SIZE) {
      writeRawCookie(name, encoded);
      deleteChunks(name); // clean up any stale chunked variant
    } else {
      // Chunked write. Clear the single-cookie slot so getItem doesn't
      // race against a stale fast-path value.
      deleteRawCookie(name);
      const total = Math.ceil(encoded.length / CHUNK_SIZE);
      for (let i = 0; i < total && i < MAX_CHUNKS; i++) {
        const slice = encoded.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        writeRawCookie(`${name}.${i}`, slice);
      }
      // Trim any leftover chunks from a previous larger session.
      for (let i = total; i < MAX_CHUNKS; i++) {
        const k = `${name}.${i}`;
        if (readRawCookie(k) == null) break;
        deleteRawCookie(k);
      }
    }
    // Belt-and-suspenders: mirror the session to localStorage too. If
    // the cookie write was silently rejected by the browser (size, ITP,
    // tracking-protection policy), localStorage at least keeps the
    // current origin logged-in; the cross-subdomain hop only breaks
    // for users whose browsers refuse the cookie outright.
    try { localStorage?.setItem(name, value); } catch {}
  },
  removeItem(name) {
    if (typeof document === 'undefined') return;
    deleteRawCookie(name);
    deleteChunks(name);
    try { localStorage?.removeItem(name); } catch {}
  },
};

// One-time migration: copy any existing Supabase session in localStorage
// over to the .meetepio.com cookie so users who were signed in before
// the apex/subdomain split don't get bounced back to the landing page.
// Runs only on meetepio.com hosts and only when there's actually a
// session key in localStorage. Idempotent — re-running does nothing.
//
// Crucial: verify the cookie was actually written before deleting the
// localStorage copy. Browsers silently reject oversized cookies, so a
// naive write-then-delete migration was destroying sessions for users
// whose JSON happened to exceed the per-cookie limit.
function migrateLocalStorageSessionToCookies() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith('sb-') || !k.endsWith('-auth-token')) continue;
      // Only migrate if the cookie isn't already populated — otherwise
      // we'd overwrite a fresh cookie with a stale localStorage copy.
      if (cookieStorage.getItem(k) != null) continue;
      const v = localStorage.getItem(k);
      if (!v) continue;
      cookieStorage.setItem(k, v);
      // setItem also mirrors to localStorage, so after a successful
      // write the key is still in localStorage as a fallback — no
      // need to remove it explicitly.
    }
  } catch {
    // Quota / access errors — fall through. Worst case the user signs
    // in again, which is the pre-migration baseline anyway.
  }
}

const useCookieStorage = (typeof window !== 'undefined') && isMeetepioHost();
if (useCookieStorage) migrateLocalStorageSessionToCookies();

export const supabase = (url && key)
  ? createClient(url, key, useCookieStorage ? { auth: { storage: cookieStorage } } : undefined)
  : null

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function signUp(email, password, meta = {}) {
  if (!supabase) throw new Error('Auth not configured. Contact support.')
  // first_name / last_name go into auth user_metadata — schemaless, so
  // it's always safe even if the profile table doesn't have those
  // columns yet. The real app reads these off the user to pre-fill
  // profiles after signup.
  const authMeta = { user_type: meta.user_type || 'podcaster' }
  if (meta.full_name)  authMeta.full_name  = meta.full_name
  if (meta.first_name) authMeta.first_name = meta.first_name
  if (meta.last_name)  authMeta.last_name  = meta.last_name

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: authMeta }
  })
  if (error) throw error

  // Create profile row immediately on signup
  // Only use columns that exist in the base schema
  if (data.user) {
    const profileData = {
      user_id: data.user.id,
      email,
      show_name: meta.show_name || '',
      niche: meta.niche || '',
      // Mini-spec #59: default new podcasters to PUBLIC so they appear
      // in Explore. Was true only for advertisers. Users opt-out via
      // the public/private toggle in the profile editor.
      marketplace_visible: true,
      episodes_created: 0,
      created_at: new Date().toISOString(),
    }
    // Add full_name only if provided (column might not exist yet on older DBs)
    if (meta.full_name) profileData.full_name = meta.full_name

    const { error: profileError } = await supabase
      .from('podcaster_profiles')
      .upsert(profileData, { onConflict: 'user_id' })
    if (profileError) console.error('Profile creation error:', profileError)
  }
  return data
}

export async function signIn(email, password) {
  if (!supabase) throw new Error('Auth not configured. Contact support.')
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

export async function signInWithGoogle(opts) {
  return signInWithProvider('google', opts)
}

// Generic OAuth sign-in. Supabase needs each provider turned on in the
// dashboard with its client id/secret pasted in.
//
// `opts.returnTo` lets a caller force a specific landing destination
// after the OAuth round-trip. The landing-v2 modals pass `'landing-v2'`
// so the user comes back to the landing (with their locked doc waiting)
// instead of the main-app dashboard. Implemented as a `?lv2=1` query
// flag the app router reads + strips on first load.
export async function signInWithProvider(provider, opts = {}) {
  if (!supabase) throw new Error('Auth not configured. Contact support.')
  // After the OAuth round-trip, send users to the app subdomain in
  // production (so the session lands directly on the authenticated
  // surface). In dev / preview / legacy hosts authRedirectBase()
  // returns the current origin and the existing behavior stands.
  const base = authRedirectBase()
  const redirectTo = opts.returnTo === 'landing-v2'
    ? `${base}/?lv2=1`
    : base
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo }
  })
  if (error) throw error
}

export async function signOut() {
  if (supabase) await supabase.auth.signOut()
}

// Mini-spec #15 — password reset. Sends a magic-link email via Supabase
// that lands the user back on /reset-password (handled by the standard
// Supabase recovery flow). In production the link routes to the app
// subdomain; in dev / preview it uses the current origin so flows keep
// working without DNS for app.*.
export async function sendPasswordReset(email) {
  if (!supabase) throw new Error('Auth not configured. Contact support.')
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${authRedirectBase()}/reset-password`,
  })
  if (error) throw error
}

// ── Profile ───────────────────────────────────────────────────────────────────

export async function getProfile(userId) {
  if (!supabase || !userId) return null
  const { data, error } = await supabase
    .from('podcaster_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) { console.error('getProfile error:', error); return null }

  // Merge auth user metadata into profile so full_name is always available
  // even if the DB schema cache hasn't picked up the new column yet.
  // Read from the cached session (local, no network) rather than
  // getUser() (a round-trip to GoTrue) — this runs on every profile
  // load and gated the app's initial render on an extra request.
  if (data) {
    const { data: { session } } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }))
    const meta = session?.user?.user_metadata || {}
    if (meta.full_name  && !data.full_name)  data.full_name  = meta.full_name
    if (meta.username   && !data.username)   data.username   = String(meta.username).toLowerCase()
    if (meta.avatar_url && !data.avatar_url) data.avatar_url = meta.avatar_url
  }
  return data
}

// Tier 1 — all extended fields (requires migration to have been run)
const EXTENDED_FIELDS = [
  'full_name', 'username', 'plan', 'user_type',
  'cover_image_url', 'open_to_guests', 'media_kit_url',
  'social_followers', 'professional_title',
  'verified', 'verification_requested_at', 'podcast_clips',
  // Mini-spec #3 (migration 024): profile editor fields
  'niches', 'open_to_guesting', 'open_to_inviting', 'x_url',
  // Mini-spec #7 (migration 028): morning briefing settings
  'timezone', 'morning_briefing_opt_in',
  // Mini-spec #9 (migration 030): leaderboard opt-in
  'leaderboard_opt_in',
  // Mini-spec #13 (migration 031): profile portfolio v2
  'profile_banner_url', 'audio_sample_url', 'audio_sample_title',
  'slices', 'past_appearances', 'past_guests', 'professional_title',
  // OnboardingFlow fields — the takeover's `updateProfile` call writes
  // these on completion. Missing from the whitelist meant they were
  // silently dropped, so `onboarding_complete` never persisted and the
  // takeover re-appeared on every token refresh.
  'onboarding_complete', 'show_status', 'reactivation_user',
]

// Tier 2 — fields that exist in the original schema
const BASE_FIELDS = [
  'show_name', 'niche', 'bio',
  'youtube_url', 'instagram_url', 'spotify_url', 'tiktok_url', 'website_url',
  'avatar_url', 'marketplace_visible', 'episodes_created',
]

// Tier 3 — absolute minimum, guaranteed on any valid podcaster_profiles table
const MINIMAL_FIELDS = ['email', 'show_name', 'niche', 'avatar_url']

async function upsertProfile(userId, payload) {
  // Never include updated_at — the DB trigger sets it automatically.
  // Forcing it causes schema-cache errors when PostgREST hasn't refreshed yet.
  const { updated_at: _drop, ...safe } = payload
  return supabase
    .from('podcaster_profiles')
    .upsert(
      { user_id: userId, ...safe },
      { onConflict: 'user_id' }
    )
    .select()
    .maybeSingle()
}

export async function updateProfile(userId, updates) {
  if (!supabase || !userId) return null

  // Always persist key fields to auth user metadata — works regardless of DB
  // schema cache state, so avatar/name always display even if DB write fails
  const metaUpdate = {}
  if (updates.full_name  !== undefined) metaUpdate.full_name  = updates.full_name
  if (updates.username   !== undefined) metaUpdate.username   = updates.username
  if (updates.avatar_url !== undefined) metaUpdate.avatar_url = updates.avatar_url
  if (Object.keys(metaUpdate).length > 0) {
    await supabase.auth.updateUser({ data: metaUpdate }).catch(() => {})
  }

  const ALL_FIELDS = [...BASE_FIELDS, ...EXTENDED_FIELDS]
  const clean = Object.fromEntries(
    Object.entries(updates).filter(([k, v]) => v !== undefined && ALL_FIELDS.includes(k))
  )
  // Surface fields that were filtered out — most often this is a typo or
  // a column the editor sends that doesn't exist in the schema lists, and
  // it explains "I typed in the editor but nothing persisted."
  const droppedKeys = Object.keys(updates).filter(k =>
    updates[k] !== undefined && !ALL_FIELDS.includes(k)
  )
  if (droppedKeys.length > 0) {
    console.warn('[updateProfile] dropped unknown fields:', droppedKeys)
  }

  // Tier 1 — try with all fields. If a column is missing, the previous
  // implementation stripped EVERY extended field on the retry, which
  // silently dropped username / full_name when any single optional
  // column (professional_title, audio_sample_url, etc.) was missing.
  // The user would see "Profile saved" but the handle never wrote.
  //
  // New strategy: iterative column-by-column drop. Parse the offending
  // column out of each schema-cache error, drop ONLY that column,
  // retry. Username / full_name survive as long as those specific
  // columns exist on the user's DB.
  let { data: row, dropped } = await upsertWithIterativeDrop(userId, clean)

  if (row == null) {
    console.error('[updateProfile] upsert returned no row — likely an RLS read-after-write block. Sent fields:', Object.keys(clean))
    throw new Error('Save did not persist (the row came back empty after upsert). Check your podcaster_profiles RLS policies for SELECT/UPDATE on auth.uid() = user_id.')
  }

  // Critical fields the user typed and expects to see persisted —
  // if any of these got dropped during the column-fallback dance, we
  // must surface a real error so the user knows the handle / name
  // change didn't actually save instead of seeing a misleading
  // "Profile saved" toast.
  const CRITICAL_FIELDS = ['username', 'full_name']
  const criticalDropped = dropped.filter(f =>
    CRITICAL_FIELDS.includes(f) && updates[f] !== undefined
  )
  if (criticalDropped.length > 0) {
    throw new Error(
      `Couldn't save ${criticalDropped.join(', ')} — the podcaster_profiles table is missing required columns. Run pending Supabase migrations.`
    )
  }
  if (dropped.length > 0) {
    console.warn('[updateProfile] some columns missing in schema — pending migrations may be needed:', dropped)
  }
  return row
}

// Pulls the offending column name out of a Postgres / PostgREST schema-
// cache error message. Returns null if the message doesn't match any
// known pattern (in which case the caller falls back to a wider drop).
function extractMissingColumn(msg) {
  if (!msg) return null
  // "Could not find the 'foo' column of 'bar' in the schema cache"
  let m = msg.match(/['"]([\w_]+)['"]\s+column\s+of/i)
  if (m) return m[1]
  // "column \"foo\" of relation \"bar\" does not exist"
  m = msg.match(/column\s+["']?([\w_]+)["']?\s+of/i)
  if (m) return m[1]
  // "column foo does not exist"
  m = msg.match(/column\s+["']?([\w_]+)["']?\s+does\s+not\s+exist/i)
  if (m) return m[1]
  return null
}

// Iteratively retry the upsert, dropping one missing column at a time
// when the DB returns a schema-cache miss. Returns { data, dropped }
// where `dropped` is the list of columns that had to be removed.
// Throws on non-schema errors. Safety cap of 12 iterations.
async function upsertWithIterativeDrop(userId, fields) {
  let attempt = { ...fields }
  const dropped = []
  for (let i = 0; i < 12; i++) {
    const { data, error } = await upsertProfile(userId, attempt)
    if (!error) return { data, dropped }
    if (!isSchemaError(error)) {
      throw new Error(friendlyError(error))
    }
    const col = extractMissingColumn(error.message)
    if (col && col in attempt) {
      const { [col]: _drop, ...rest } = attempt
      attempt = rest
      dropped.push(col)
      continue
    }
    // Couldn't pinpoint the column — drop every non-base field at once
    // as a last-ditch retry (the old tier-2 behavior). After this, if
    // it still fails, we surface the original error.
    const baseOnly = Object.fromEntries(
      Object.entries(attempt).filter(([k]) => BASE_FIELDS.includes(k))
    )
    if (Object.keys(baseOnly).length === Object.keys(attempt).length) {
      // Already at base — nothing more to strip.
      throw new Error(friendlyError(error))
    }
    const newlyDropped = Object.keys(attempt).filter(k => !BASE_FIELDS.includes(k))
    attempt = baseOnly
    dropped.push(...newlyDropped)
  }
  throw new Error('Too many schema mismatches — aborting profile upsert.')
}

function isSchemaError(error) {
  const msg = error?.message || ''
  return (
    msg.includes('column') && msg.includes('does not exist') ||
    msg.includes('schema cache') ||
    msg.includes('Could not find')
  )
}

function friendlyError(error) {
  const msg = error?.message || 'Unknown error'
  if (isSchemaError(error)) {
    return `Database needs updating. Go to Supabase → SQL Editor and run the migration SQL from the README. (${msg})`
  }
  return msg
}

// ── Creator voice profile (Chat Upgrade Spec §3.3) ─────────────────────────────
// One row per creator in creator_voice_profiles, keyed to the
// podcaster_profiles.id (the spec's generic `profiles(id)`). Read at
// conversation start; refined at episode completion. Callers NEVER touch
// supabase.from('creator_voice_profiles') directly — they go through these
// two helpers, same as profile writes go through updateProfile().

const VOICE_TEXT_FIELDS  = ['register', 'rhythm', 'audience', 'audience_state', 'primary_genre', 'sub_genre']
const VOICE_ARRAY_FIELDS = ['vocabulary', 'signature_moves', 'avoid_list']

export async function getVoiceProfile(profileId) {
  if (!supabase || !profileId) return null
  const { data, error } = await supabase
    .from('creator_voice_profiles')
    .select('*')
    .eq('profile_id', profileId)
    .maybeSingle()
  if (error) {
    // Migration 052 not applied yet → behave like "no profile" rather
    // than blowing up the caller (matches getProfile()'s null-on-error).
    if (!isSchemaError(error)) console.error('getVoiceProfile error:', error)
    return null
  }
  return data
}

// Normalize a jsonb array column (could be null / array / stringified)
// into a clean, deduped array of trimmed strings.
function asStringArray(v) {
  let arr = v
  if (typeof arr === 'string') { try { arr = JSON.parse(arr) } catch { arr = [] } }
  if (!Array.isArray(arr)) return []
  const seen = new Set()
  const out = []
  for (const item of arr) {
    const s = typeof item === 'string' ? item.trim() : (item == null ? '' : String(item).trim())
    if (s && !seen.has(s.toLowerCase())) { seen.add(s.toLowerCase()); out.push(s) }
  }
  return out
}

async function upsertVoiceRow(profileId, payload) {
  // Never send updated_at — the creator_voice_profiles_updated trigger
  // sets it (same reason as upsertProfile()).
  const { updated_at: _drop, ...safe } = payload
  return supabase
    .from('creator_voice_profiles')
    .upsert(
      { profile_id: profileId, ...safe },
      { onConflict: 'profile_id' }
    )
    .select()
    .maybeSingle()
}

// Refine (never overwrite) the creator's voice profile at episode
// completion. Merge rules per spec §3.3:
//   • vocabulary / signature_moves / avoid_list → append + dedupe
//   • register → set on first capture; a later CONTRADICTING register is
//     kept as-is (not clobbered) so a later step can ask to confirm it
//   • other text fields → fill-if-empty, never clobber an established value
//   • episodes_count → +1 each completion
// Mirrors updateProfile()'s 3-tier upsert. This is a fire-and-forget
// background write at build completion, so a hard failure is logged and
// returns null — it never throws into the build flow.
export async function upsertVoiceProfile(profileId, signals = {}) {
  if (!supabase || !profileId) return null

  const current = await getVoiceProfile(profileId)

  const merged = {}

  for (const f of VOICE_ARRAY_FIELDS) {
    const next = asStringArray(current?.[f]).concat(asStringArray(signals[f]))
    merged[f] = asStringArray(next)
  }

  // register: first capture wins; a contradicting later register is
  // preserved (not overwritten) and left for next-time confirmation.
  const incomingRegister = (signals.register || '').trim()
  if (!current?.register && incomingRegister) {
    merged.register = incomingRegister
  } else if (current?.register) {
    merged.register = current.register
  }

  // remaining text fields: only fill when there's no established value.
  for (const f of VOICE_TEXT_FIELDS) {
    if (f === 'register') continue
    const incoming = (signals[f] || '').trim?.() ?? ''
    if (current?.[f]) merged[f] = current[f]
    else if (incoming) merged[f] = incoming
  }

  merged.episodes_count = (Number(current?.episodes_count) || 0) + 1

  // Tier 1 — full merged payload.
  const { data, error } = await upsertVoiceRow(profileId, merged)
  if (!error) {
    if (data == null) {
      // RLS read-after-write block. Background write — log, don't throw.
      console.error('[upsertVoiceProfile] upsert returned no row (likely RLS SELECT/UPDATE block on creator_voice_profiles).')
      return null
    }
    return data
  }

  if (isSchemaError(error)) {
    // Tier 2 — a column may be missing; write the core only.
    const core = { episodes_count: merged.episodes_count }
    if (merged.register) core.register = merged.register
    const { data: d2, error: e2 } = await upsertVoiceRow(profileId, core)
    if (!e2) {
      console.warn('[upsertVoiceProfile] tier-2 fallback used. Run supabase-migrations/052_creator_voice_profiles.sql to persist the full voice profile.')
      return d2
    }
    if (isSchemaError(e2)) {
      // Tier 3 — minimal row so the link at least exists.
      const { data: d3, error: e3 } = await upsertVoiceRow(profileId, {})
      if (!e3) return d3
      console.warn('[upsertVoiceProfile] MIGRATION_NEEDED: ' + (e3.message || e2.message || error.message))
      return null
    }
    console.error('[upsertVoiceProfile] error:', e2)
    return null
  }
  console.error('[upsertVoiceProfile] error:', error)
  return null
}

export async function checkUsername(username, currentUserId) {
  if (!supabase || !username) return { available: false, error: 'Username required' }

  // Validate format first
  if (!/^[a-zA-Z0-9_]{3,30}$/.test(username)) {
    return { available: false, error: 'Username must be 3–30 characters, letters, numbers, and underscores only' }
  }

  try {
    const { data, error } = await supabase
      .from('podcaster_profiles')
      .select('user_id')
      .eq('username', username.toLowerCase())
      .maybeSingle()

    if (error) {
      // Username column doesn't exist yet — silently skip the check
      if (isSchemaError(error)) {
        return { available: true, error: null, migrationNeeded: true }
      }
      return { available: false, error: error.message }
    }

    if (data && data.user_id !== currentUserId) return { available: false, error: 'Username already taken' }
    return { available: true, error: null }
  } catch {
    return { available: true, error: null }
  }
}

export async function uploadAvatar(userId, file) {
  if (!supabase || !userId || !file) throw new Error('Missing required params')

  const ext = file.name.split('.').pop().toLowerCase()
  const allowed = ['jpg', 'jpeg', 'png', 'webp', 'gif']
  if (!allowed.includes(ext)) throw new Error('Image must be JPG, PNG, WebP or GIF')
  if (file.size > 5 * 1024 * 1024) throw new Error('Image must be under 5MB')

  const path = `avatars/${userId}.${ext}`
  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type })

  if (uploadError) {
    if (uploadError.message?.toLowerCase().includes('bucket')) {
      throw new Error('Storage buckets not set up. Run supabase-migrations/032_storage_buckets.sql in the Supabase SQL editor (one-time).')
    }
    throw uploadError
  }

  const { data } = supabase.storage.from('avatars').getPublicUrl(path)
  return data.publicUrl + '?v=' + Date.now()
}

/**
 * Upload a cover image for a show. Requires a public Supabase Storage bucket
 * named `show-covers`. Path is scoped per-user-per-show so users can't
 * collide with each other and we can wipe a user's assets on delete if needed.
 */
export async function uploadShowCover(userId, showId, file) {
  if (!supabase || !userId || !showId || !file) throw new Error('Missing required params')

  const ext = (file.name.split('.').pop() || 'png').toLowerCase()
  const allowed = ['jpg', 'jpeg', 'png', 'webp', 'gif']
  if (!allowed.includes(ext)) throw new Error('Image must be JPG, PNG, WebP or GIF')
  if (file.size > 5 * 1024 * 1024) throw new Error('Image must be under 5MB')

  const path = `${userId}/${showId}.${ext}`
  const { error: uploadError } = await supabase.storage
    .from('show-covers')
    .upload(path, file, { upsert: true, contentType: file.type })

  if (uploadError) {
    if (uploadError.message?.toLowerCase().includes('bucket')) {
      throw new Error('Storage bucket not set up. Go to Supabase → Storage → create a PUBLIC bucket named "show-covers".')
    }
    throw uploadError
  }

  const { data } = supabase.storage.from('show-covers').getPublicUrl(path)
  // Cache-bust so the new image appears immediately without a refresh
  return data.publicUrl + '?v=' + Date.now()
}

/**
 * Upload a profile cover/banner image.
 * Requires a PUBLIC Supabase Storage bucket named "profile-covers".
 */
export async function uploadProfileCover(userId, file) {
  if (!supabase || !userId || !file) throw new Error('Missing required params');
  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
  const allowed = ['jpg', 'jpeg', 'png', 'webp'];
  if (!allowed.includes(ext)) throw new Error('Image must be JPG, PNG, or WebP');
  if (file.size > 8 * 1024 * 1024) throw new Error('Cover image must be under 8 MB');
  const path = `${userId}/cover.${ext}`;
  const { error: uploadError } = await supabase.storage
    .from('profile-covers')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (uploadError) {
    if (uploadError.message?.toLowerCase().includes('bucket'))
      throw new Error('Storage bucket not set up. Create a PUBLIC bucket named "profile-covers" in Supabase console.');
    throw uploadError;
  }
  const { data } = supabase.storage.from('profile-covers').getPublicUrl(path);
  return data.publicUrl + '?v=' + Date.now();
}

// ── Episodes ──────────────────────────────────────────────────────────────────

// Generate URL-safe slug from topic + timestamp suffix
function generateSlug(topic) {
  const base = (topic || 'episode')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 48);
  const suffix = Date.now().toString(36).slice(-5);
  return `${base}-${suffix}`;
}

// True for errors that look like a missing/unknown column — used to
// decide whether to retry with a stripped-down payload vs propagate.
function isColumnError(err) {
  if (!err) return false;
  const msg = (err.message || '').toLowerCase();
  return (
    msg.includes('column') ||
    msg.includes('schema cache') ||
    msg.includes('could not find') ||
    err.code === 'PGRST204' ||
    err.code === '42703'
  );
}
function stripOptionalColumns(payload, columnsToDrop) {
  const out = { ...payload };
  columnsToDrop.forEach(c => { delete out[c]; });
  return out;
}

export async function saveEpisode(userId, episodeData) {
  if (!supabase) {
    console.warn('[Epio] Supabase not configured — episode not saved');
    return null;
  }

  const { topic, format, niche, result, show_id, remixed_from_id, workspace_id } = episodeData;
  const slug = generateSlug(topic);

  const payload = {
    user_id: userId,
    topic:  topic  || '',
    format: format || 'solo',
    niche:  niche  || '',
    result: result || {},
    tool:   'episode',
    slug,
    created_at: new Date().toISOString(),
  };
  if (show_id)         payload.show_id         = show_id;
  if (remixed_from_id) payload.remixed_from_id = remixed_from_id;
  if (workspace_id)    payload.workspace_id    = workspace_id;

  // Cascade-strip fallback. If the first insert fails because a column
  // doesn't exist, retry with progressively smaller payloads — first
  // drop optional columns, then drop everything except the absolute
  // minimum the episodes table is guaranteed to have. The previous
  // version only stripped `slug / show_id / remixed_from_id`, so a
  // missing `workspace_id` or unknown column would still 400 even
  // though we have a perfectly valid base payload to fall back to.
  let row;
  let { data: r1, error: e1 } = await supabase
    .from('episodes')
    .insert(payload)
    .select()
    .single();
  row = r1;
  let lastErr = e1;

  if (e1 && isColumnError(e1)) {
    // Tool, slug, show_id, etc. were added by later migrations and not
    // every DB has them yet. Strip every optional column so the row
    // lands on a base schema.
    const tier2 = stripOptionalColumns(payload, ['slug', 'show_id', 'remixed_from_id', 'workspace_id', 'tool']);
    const { data: r2, error: e2 } = await supabase
      .from('episodes')
      .insert(tier2)
      .select()
      .single();
    row = r2;
    lastErr = e2;
    console.warn('[saveEpisode] tier-2 fallback used (optional columns dropped). Pending migrations may be needed:',
      Object.keys(payload).filter(k => !(k in tier2)));
  }
  if (lastErr && isColumnError(lastErr)) {
    // Tier 3 — drop everything optional, keep only the absolute minimum
    // we know is in the original episodes schema. `tool` is NOT here
    // because the "Could not find the 'tool' column" error is exactly
    // the case this tier is supposed to rescue.
    const tier3 = {
      user_id:    payload.user_id,
      topic:      payload.topic,
      format:     payload.format,
      result:     payload.result,
      created_at: payload.created_at,
    };
    const { data: r3, error: e3 } = await supabase
      .from('episodes')
      .insert(tier3)
      .select()
      .single();
    row = r3;
    lastErr = e3;
    console.warn('[saveEpisode] tier-3 fallback used (kept only minimum required columns).');
  }

  if (lastErr) {
    // Provide actionable error messages but ALSO include the raw
    // Supabase message so the user can diagnose / forward to support.
    const raw = lastErr.message || lastErr.code || 'unknown error';
    if (lastErr.message?.includes('RLS') || lastErr.message?.includes('policy') || lastErr.code === '42501') {
      throw new Error(`Permission denied (RLS) — run pending migrations in Supabase SQL Editor. [${raw}]`);
    }
    if (lastErr.message?.includes('JWT') || lastErr.message?.includes('token')) {
      throw new Error('Session expired — please sign in again');
    }
    throw new Error(`Episode save failed: ${raw}`);
  }

  // Increment counter — non-fatal. supabase.rpc(...) returns a thenable
  // QueryBuilder, NOT a real Promise, so chaining `.catch` on it throws
  // a synchronous "catch is not a function" TypeError. Use try / await
  // / catch so a missing RPC or any other failure stays a soft warning
  // and never propagates up to fail the save.
  try {
    const { error: incErr } = await supabase.rpc('increment_episodes', { uid: userId });
    if (incErr) console.warn('increment_episodes failed:', incErr.message);
  } catch (e) {
    console.warn('increment_episodes threw:', e?.message || e);
  }

  return row;
}

// Persist a generated short-form script. Uses the same `episodes` table as
// long-form builds (with tool='short_form') so workspace grouping, listings,
// and the deletion path are shared. result is the raw script payload from
// /api/short-form. workspace_id is optional but recommended (default flow
// passes the user's "Short-form" workspace id).
export async function saveShortFormScript(userId, scriptData) {
  if (!supabase) {
    console.warn('[Epio] Supabase not configured — short-form script not saved');
    return null;
  }
  const { topic, tone, hook_style, target_seconds, result, workspace_id } = scriptData;
  const payload = {
    user_id:    userId,
    topic:      topic || '',
    format:     'short_form',
    niche:      '',
    result:     result || {},
    tool:       'short_form',
    created_at: new Date().toISOString(),
  };
  if (workspace_id) payload.workspace_id = workspace_id;
  // Stash extras in result so the row stays self-describing without
  // requiring schema changes.
  if (tone || hook_style || target_seconds) {
    payload.result = {
      ...payload.result,
      _meta: { tone, hook_style, target_seconds },
    };
  }

  let { data: r1, error: e1 } = await supabase
    .from('episodes')
    .insert(payload)
    .select()
    .single();

  if (e1 && isColumnError(e1)) {
    // Same as saveEpisode: `tool` was added by a later migration so
    // strip it (and workspace_id) when the schema is behind.
    const tier2 = stripOptionalColumns(payload, ['workspace_id', 'tool']);
    const { data: r2, error: e2 } = await supabase
      .from('episodes')
      .insert(tier2)
      .select()
      .single();
    if (!e2) return r2;
    console.warn('[saveShortFormScript] tier-2 fallback failed:', e2.message);
  }
  if (e1) {
    console.error('saveShortFormScript error:', e1);
    return null;
  }
  return r1;
}

export async function getEpisodes(userId) {
  if (!supabase || !userId) return []
  // Try with show_id first (migration 001). Fall back to base columns for
  // any DB that hasn't run the shows migration yet.
  // Filter to tool='episode' so short-form scripts (tool='short_form')
  // stay out of long-form listings — they live under their own workspace
  // category and have a separate surface.
  let { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, niche, result, created_at, show_id, workspace_id')
    .eq('user_id', userId)
    .eq('tool', 'episode')
    .is('trashed_at', null)          // hide trashed episodes (migration 064)
    .order('created_at', { ascending: false })
    .limit(200)
  if (error && error.message?.includes('show_id')) {
    const retry = await supabase
      .from('episodes')
      .select('id, topic, format, niche, result, created_at, workspace_id')
      .eq('user_id', userId)
      .eq('tool', 'episode')
      .order('created_at', { ascending: false })
      .limit(200)
    data = retry.data
    error = retry.error
  }
  // Fallback for DBs missing a later-migration column (e.g. `tool` or
  // `trashed_at`). Drop the optional filters so the library still
  // populates; keep show_id + workspace_id so folders keep working.
  if (error && isColumnError(error)) {
    const retry = await supabase
      .from('episodes')
      .select('id, topic, format, niche, result, created_at, show_id, workspace_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(200)
    data = retry.data
    error = retry.error
  }
  if (error) { console.error('getEpisodes error:', error); return [] }
  return data || []
}

// Short-form script equivalent of getEpisodes. Same `episodes` table,
// filtered to tool='short_form'. Lives in this module (not its own
// short-form lib) so the workspace right-panel can load both lists in
// one parallel fetch and grouped them under their respective folders.
export async function getShortFormScripts(userId) {
  if (!supabase || !userId) return []
  // tool column missing or RLS error → silently return [] rather than
  // bubbling a red banner. Mirrors getEpisodes' soft-fail behavior.
  let { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, niche, result, created_at, show_id, workspace_id')
    .eq('user_id', userId)
    .eq('tool', 'short_form')
    .is('trashed_at', null)          // hide trashed (migration 064)
    .order('created_at', { ascending: false })
    .limit(200)
  // Pre-064: trashed_at column missing → retry without the filter so the
  // list still loads (there's nothing trashed yet anyway).
  if (error && isColumnError(error)) {
    const retry = await supabase
      .from('episodes')
      .select('id, topic, format, niche, result, created_at, show_id, workspace_id')
      .eq('user_id', userId)
      .eq('tool', 'short_form')
      .order('created_at', { ascending: false })
      .limit(200)
    data = retry.data
    error = retry.error
  }
  if (error) {
    if (!isColumnError(error)) console.error('getShortFormScripts error:', error)
    return []
  }
  return data || []
}

// ── Episode delete (single + bulk) ──────────────────────────────────────────

// Delete = move to Trash (soft-delete, recoverable). Falls back to a real
// delete if the trashed_at column doesn't exist yet (migration 064 not run),
// so deleting always works — it just becomes recoverable once 064 lands.
export async function deleteEpisode(userId, episodeId) {
  if (!supabase || !userId || !episodeId) throw new Error('Missing ids')
  const { data, error } = await supabase
    .from('episodes')
    .update({ trashed_at: new Date().toISOString() })
    .eq('id', episodeId)
    .eq('user_id', userId)
    .select()
  if (error && isColumnError(error)) return hardDeleteEpisode(userId, episodeId)
  if (error) { console.error('deleteEpisode error:', error); throw error }
  if (!data || data.length === 0) {
    throw new Error('Delete blocked by database permissions. Run the latest migration in Supabase SQL Editor.')
  }
  return data[0]
}

/** Bulk move-to-trash. Returns the affected rows. */
export async function deleteEpisodes(userId, episodeIds = []) {
  if (!supabase || !userId) throw new Error('Not authenticated')
  if (!episodeIds.length) return []
  const { data, error } = await supabase
    .from('episodes')
    .update({ trashed_at: new Date().toISOString() })
    .in('id', episodeIds)
    .eq('user_id', userId)
    .select('id')
  if (error && isColumnError(error)) return hardDeleteEpisodes(userId, episodeIds)
  if (error) { console.error('deleteEpisodes error:', error); throw error }
  return data || []
}

// ── Trash ────────────────────────────────────────────────────────────────────

/** Trashed episodes (long-form), newest-trashed first. */
export async function getTrashedEpisodes(userId) {
  if (!supabase || !userId) return []
  const { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, niche, result, created_at, show_id, workspace_id, trashed_at')
    .eq('user_id', userId)
    .not('trashed_at', 'is', null)
    .order('trashed_at', { ascending: false })
    .limit(200)
  if (error) { if (!isColumnError(error)) console.error('getTrashedEpisodes error:', error); return [] }
  return data || []
}

/** Restore trashed episodes back to their folder (clears trashed_at). */
export async function restoreEpisodes(userId, episodeIds = []) {
  if (!supabase || !userId || !episodeIds.length) return []
  const { data, error } = await supabase
    .from('episodes')
    .update({ trashed_at: null })
    .in('id', episodeIds)
    .eq('user_id', userId)
    .select('id')
  if (error) { console.error('restoreEpisodes error:', error); throw error }
  return data || []
}

/** Permanently delete one episode (used by the soft-delete fallback). */
export async function hardDeleteEpisode(userId, episodeId) {
  const { data, error } = await supabase
    .from('episodes').delete().eq('id', episodeId).eq('user_id', userId).select()
  if (error) { console.error('hardDeleteEpisode error:', error); throw error }
  return data?.[0]
}

/** Permanently delete episodes ("Delete forever" / "Empty trash"). */
export async function hardDeleteEpisodes(userId, episodeIds = []) {
  if (!supabase || !userId || !episodeIds.length) return []
  const { data, error } = await supabase
    .from('episodes').delete().in('id', episodeIds).eq('user_id', userId).select('id')
  if (error) { console.error('hardDeleteEpisodes error:', error); throw error }
  return data || []
}

/**
 * Lazy auto-purge: permanently delete anything trashed more than 28 days ago.
 * Called when the user opens the Library. Safe no-op pre-064. Returns count.
 */
export async function purgeOldTrash(userId) {
  if (!supabase || !userId) return 0
  const cutoff = new Date(Date.now() - 28 * 86400000).toISOString()
  const { data, error } = await supabase
    .from('episodes')
    .delete()
    .eq('user_id', userId)
    .lt('trashed_at', cutoff)
    .select('id')
  if (error) { if (!isColumnError(error)) console.error('purgeOldTrash error:', error); return 0 }
  return (data || []).length
}

// Move episodes into a workspace (folder). Pass workspaceId = null to remove
// them from any folder ("unsort"). Returns the affected rows.
export async function moveEpisodesToWorkspace(userId, episodeIds = [], workspaceId = null) {
  if (!supabase || !userId) throw new Error('Not authenticated')
  if (!episodeIds.length) return []
  const { data, error } = await supabase
    .from('episodes')
    .update({ workspace_id: workspaceId })
    .in('id', episodeIds)
    .eq('user_id', userId)
    .select('id')
  if (error) { console.error('moveEpisodesToWorkspace error:', error); throw error }
  return data || []
}

// Rename = update the display title (topic). Covers episodes and short-form
// alike since both live in the episodes table.
export async function renameEpisode(userId, episodeId, topic) {
  if (!supabase || !userId || !episodeId) throw new Error('Missing ids')
  const next = (topic || '').trim()
  if (!next) throw new Error('Title required')
  const { data, error } = await supabase
    .from('episodes')
    .update({ topic: next })
    .eq('id', episodeId)
    .eq('user_id', userId)
    .select('id, topic')
  if (error) { console.error('renameEpisode error:', error); throw error }
  if (!data || data.length === 0) throw new Error('Rename blocked (permissions or missing row)')
  return data[0]
}

// ── Guest usage ───────────────────────────────────────────────────────────────

const GUEST_KEY = 'eai_guest_uses'
export const FREE_LIMIT = 3

export function getGuestUses() {
  try { return parseInt(localStorage.getItem(GUEST_KEY) || '0') } catch { return 0 }
}
export function incGuestUses() {
  try { localStorage.setItem(GUEST_KEY, String(getGuestUses() + 1)) } catch {}
}

// ── Show Templates ────────────────────────────────────────────────────────────

export async function getShowTemplates(userId) {
  if (!supabase || !userId) return [];
  const { data, error } = await supabase
    .from('show_templates')
    .select('*')
    .eq('user_id', userId)
    .order('use_count', { ascending: false })
    .limit(10);
  if (error) {
    if (error.message?.includes('does not exist') || error.message?.includes('relation')) {
      return []; // Table not created yet, not a fatal error
    }
    console.error('getShowTemplates error:', error);
    return [];
  }
  return data || [];
}

export async function saveShowTemplate(userId, template) {
  if (!supabase || !userId) return null;
  const payload = {
    user_id: userId,
    name: template.name || 'My Format',
    format: template.format || 'solo',
    tone: template.tone || 'calm',
    duration: template.duration || '45',
    goal: template.goal || 'views',
    audience: template.audience || '',
    listener_feeling: template.listenerFeeling || template.listener_feeling || '',
    is_default: template.is_default || false,
    updated_at: new Date().toISOString(),
  };
  const { data, error } = await supabase
    .from('show_templates')
    .insert(payload)
    .select()
    .single();
  if (error) {
    if (error.message?.includes('does not exist') || error.message?.includes('relation')) {
      return null; // Table not created yet
    }
    throw new Error(error.message);
  }
  return data;
}

export async function incrementTemplateUse(templateId) {
  if (!supabase || !templateId) return;
  // Same .catch-on-thenable issue as saveEpisode's counter. Use proper
  // try/await/catch so a missing RPC or update doesn't crash the caller.
  try {
    const { error } = await supabase.rpc('increment_template_use', { tid: templateId });
    if (!error) return;
    console.warn('increment_template_use failed:', error.message);
  } catch (e) {
    console.warn('increment_template_use threw:', e?.message || e);
  }
  // Fallback: direct update (also soft).
  try {
    await supabase.from('show_templates')
      .update({ use_count: supabase.raw('use_count + 1') })
      .eq('id', templateId);
  } catch (e) {
    console.warn('increment_template_use fallback failed:', e?.message || e);
  }
}

export async function deleteShowTemplate(templateId, userId) {
  if (!supabase || !templateId) return;
  await supabase.from('show_templates').delete()
    .eq('id', templateId).eq('user_id', userId);
}

// ── Public Episode Access ─────────────────────────────────────────────────────

export async function getPublicEpisode(slug) {
  if (!supabase || !slug) return null;
  const { data, error } = await supabase
    .from('episodes')
    .select('id, topic, format, result, slug, created_at')
    .eq('slug', slug)
    .eq('public', true)
    .maybeSingle();
  if (error) { console.error('getPublicEpisode error:', error); return null; }
  return data;
}

export async function markEpisodePublic(episodeId, userId) {
  if (!supabase || !episodeId || !userId) return null;
  const { data, error } = await supabase
    .from('episodes')
    .update({ public: true })
    .eq('id', episodeId)
    .eq('user_id', userId)
    .select('id, slug')
    .maybeSingle();
  if (error) { console.error('markEpisodePublic error:', error); return null; }
  return data;
}
